/**
 * @packageDocumentation
 * @module @yimoka/web-http/create-web-http-client
 * @summary 创建基于 axios 的 Web HTTP 客户端与 apiExecutor
 * @author keep
 * @since 0.0.1
 */

import { IAPIRequestConfig, IHTTPResponse } from '@yimoka/shared';
import type { IAPIExecutor } from '@yimoka/store';
import axios, { type InternalAxiosRequestConfig } from 'axios';

import { handleAxiosError, handleAxiosResponse } from './handle-axios-response';
import type { IWebHttpClient, IWebHttpClientOptions } from './types';

const DEFAULT_TIMEOUT = 10_000;
const DEFAULT_REALM = 'default';

type AxiosHeadersLike = InternalAxiosRequestConfig['headers'];

/** hasHeaderValue 按 HTTP 大小写不敏感语义判断请求头是否包含非空值。 */
function hasHeaderValue(headers: AxiosHeadersLike, key: string): boolean {
  const value = headers.get(key);
  if (Array.isArray(value)) {
    return value.join('').trim() !== '';
  }
  return value !== undefined && value !== null && String(value).trim() !== '';
}

/**
 * 当 header 没有有效值时才注入，避免空字符串阻断动态 Realm 等安全上下文。
 */
function setIfAbsent(headers: AxiosHeadersLike, key: string, value?: string | null): void {
  if (value === undefined || value === null || value === '') return;
  if (hasHeaderValue(headers, key)) return;
  headers.set(key, value);
}

function applyDefaultHeaders(headers: AxiosHeadersLike, defaultHeaders?: IWebHttpClientOptions['headers']): void {
  setIfAbsent(headers, 'Content-Type', 'application/json');
  if (!defaultHeaders) return;
  for (const [key, value] of Object.entries(defaultHeaders)) {
    setIfAbsent(headers, key, value);
  }
}

// applyBuiltinHeaders 在未被单次请求覆盖时补齐平台与 Realm 公共头。
function applyBuiltinHeaders(
  headers: AxiosHeadersLike,
  channel: string,
  useType: string,
  platform: string,
  realm: string,
): void {
  setIfAbsent(headers, 'x-md-global-channel', channel);
  setIfAbsent(headers, 'x-md-global-use-type', useType);
  setIfAbsent(headers, 'x-md-global-platform', platform);
  setIfAbsent(headers, 'x-md-global-realm', realm);
}

function readHeaderValue(getter?: () => string | undefined | null): string | undefined {
  return getter?.() ?? undefined;
}

/** readRealmHeader 单次读取并规范化动态 Realm，避免 getter 在同一请求内产生不一致结果。 */
function readRealmHeader(getter?: () => string | undefined | null): string | undefined {
  return getter?.()?.trim() || undefined;
}

/**
 * 动态 Realm 只覆盖客户端兜底值，保留单次请求显式 Header 的最高优先级。
 */
function applyDynamicRealmHeader(
  headers: AxiosHeadersLike,
  realm: string | undefined,
  hasRequestRealm: boolean,
): void {
  if (hasRequestRealm || !realm) return;
  headers.set('x-md-global-realm', realm);
}

function applyDynamicHeaders(headers: AxiosHeadersLike, options: IWebHttpClientOptions): void {
  const dynamicHeaders: Array<[string, string | undefined]> = [
    ['Authorization', readHeaderValue(options.getAuthorization)],
    ['x-md-global-client-id', readHeaderValue(options.getClientID)],
    ['x-md-global-tenantID', readHeaderValue(options.getTenantID)],
    ['x-md-global-language', readHeaderValue(options.getLanguage)],
    ['x-md-global-app', options.app ?? readHeaderValue(options.getApp)],
  ];

  for (const [key, value] of dynamicHeaders) {
    setIfAbsent(headers, key, value);
  }
}

/**
 * 创建 Web HTTP 客户端，返回 {@link IAPIExecutor} 与底层 axios 实例。
 *
 * @param options - 客户端配置
 * @returns 客户端实例
 *
 * @example
 * ```ts
 * // 最简：仅 baseURL
 * const { apiExecutor } = createWebHttpClient({ baseURL: 'https://api.example.com' });
 *
 * // 带默认头 + 动态 token
 * const client = createWebHttpClient({
 *   baseURL: 'https://api.example.com',
 *   headers: { 'x-md-global-language': 'zh' },
 *   getAuthorization: () => token,
 *   getTenantID: () => tenantId,
 * });
 *
 * // 复用已有 axios 实例（本包不再注入拦截器）
 * const client = createWebHttpClient({ axiosInstance: customAxios });
 * ```
 */
export function createWebHttpClient(options: IWebHttpClientOptions): IWebHttpClient {
  const {
    baseURL,
    channel = 'web',
    useType = 'client',
    platform = 'web',
    app: appOption,
    headers: defaultHeaders,
    timeout = DEFAULT_TIMEOUT,
    axiosInstance,
    axiosConfig,
    getAuthorization,
    getClientID,
    getTenantID,
    getRealm,
    getLanguage,
    getApp,
  } = options;

  const instance = axiosInstance ?? axios.create({
    baseURL,
    timeout,
    ...(axiosConfig ?? {}),
  });

  // 用户传入自定义 axios 实例时，由其自行管理 header / 拦截器，本包不介入
  if (!axiosInstance) {
    instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const { headers } = config;
      // Axios 进入拦截器时已合并单次请求头，先记录 Realm 才能避免后续动态值覆盖调用方显式选择。
      const hasRequestRealm = hasHeaderValue(headers, 'x-md-global-realm');
      const dynamicRealm = readRealmHeader(getRealm);
      applyDefaultHeaders(headers, defaultHeaders);
      applyBuiltinHeaders(headers, channel, useType, platform, dynamicRealm ?? DEFAULT_REALM);
      applyDynamicRealmHeader(headers, dynamicRealm, hasRequestRealm);
      applyDynamicHeaders(headers, {
        app: appOption,
        getApp,
        getAuthorization,
        getClientID,
        getLanguage,
        getTenantID,
      });

      return config;
    });
  }

  const apiExecutor: IAPIExecutor = async <T extends object = IAPIRequestConfig>(
    config: T,
  ): Promise<IHTTPResponse> => {
    const req = config as IAPIRequestConfig;
    try {
      const response = await instance.request({
        ...req,
        method: (req.method ?? 'GET') as string,
      });
      return handleAxiosResponse(response);
    } catch (error: unknown) {
      return handleAxiosError(error);
    }
  };

  return {
    instance,
    apiExecutor,
  };
}
